/**
 * Shopify Multi-Cart System with Share & Import Features
 * Allows customers to share carts via URL or copy cart data
 */

class MultiCartManager {
  constructor() {
    this.storageKey = 'shopify_multi_carts';
    this.currentCartKey = 'current_cart_id';
    this.init();
  }

  init() {
    // Check if there's a shared cart in the URL
    this.checkForSharedCart();
    
    if (!localStorage.getItem(this.storageKey)) {
      localStorage.setItem(this.storageKey, JSON.stringify({}));
    }
    
    const carts = this.getAllCarts();
    if (Object.keys(carts).length === 0) {
      this.createCart('My Cart');
    }
  }

  // Check URL for shared cart data
  checkForSharedCart() {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedCartData = urlParams.get('shared_cart');
    
    if (sharedCartData) {
      try {
        const decodedData = JSON.parse(atob(sharedCartData));
        this.importSharedCart(decodedData);
        
        // Remove the parameter from URL after importing
        const url = new URL(window.location);
        url.searchParams.delete('shared_cart');
        window.history.replaceState({}, '', url);
      } catch (error) {
        console.error('Invalid shared cart link:', error);
      }
    }
  }

  // Import a shared cart
  async importSharedCart(cartData) {
    const cartId = this.generateId();
    const carts = this.getAllCarts();
    
    carts[cartId] = {
      id: cartId,
      name: cartData.name + ' (Shared)',
      items: cartData.items || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.saveCarts(carts);
    
    // Show notification
    alert(`Cart "${cartData.name}" has been imported! You can view it in your carts.`);
    
    return cartId;
  }

  // Generate shareable link for a cart
  generateShareLink(cartId) {
    const cart = this.getCart(cartId);
    if (!cart) return null;

    // Create simplified cart data for sharing
    const shareData = {
      name: cart.name,
      items: cart.items,
      sharedAt: new Date().toISOString()
    };

    // Encode to base64
    const encodedData = btoa(JSON.stringify(shareData));
    
    // Generate full URL
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}?shared_cart=${encodedData}`;
    
    return shareUrl;
  }

  // Copy share link to clipboard
  async copyShareLink(cartId) {
    const shareUrl = this.generateShareLink(cartId);
    if (!shareUrl) return false;

    try {
      await navigator.clipboard.writeText(shareUrl);
      return shareUrl;
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return shareUrl;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    }
  }

  // Export cart as JSON (for manual sharing)
  exportCartData(cartId) {
    const cart = this.getCart(cartId);
    if (!cart) return null;

    const exportData = {
      name: cart.name,
      items: cart.items,
      exportedAt: new Date().toISOString()
    };

    return JSON.stringify(exportData, null, 2);
  }

  // Import cart from JSON data
  async importCartData(jsonData) {
    try {
      const cartData = JSON.parse(jsonData);
      
      const cartId = this.generateId();
      const carts = this.getAllCarts();
      
      carts[cartId] = {
        id: cartId,
        name: cartData.name + ' (Imported)',
        items: cartData.items || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      this.saveCarts(carts);
      return cartId;
    } catch (error) {
      console.error('Error importing cart:', error);
      return null;
    }
  }

  // Load cart items into current Shopify cart (for "Load to Cart" feature)
  async loadCartToShopify(cartId) {
    const cart = this.getCart(cartId);
    if (!cart || !cart.items || cart.items.length === 0) {
      alert('This cart is empty!');
      return false;
    }

    // Ask for confirmation
    if (!confirm(`Add ${cart.items.length} items from "${cart.name}" to your current cart?`)) {
      return false;
    }

    try {
      await this.addItemsToShopifyCart(cart.items);
      alert('Items added to cart successfully!');
      return true;
    } catch (error) {
      alert('Error adding items to cart. Please try again.');
      return false;
    }
  }

  generateId() {
    return 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getAllCarts() {
    return JSON.parse(localStorage.getItem(this.storageKey)) || {};
  }

  saveCarts(carts) {
    localStorage.setItem(this.storageKey, JSON.stringify(carts));
  }

  getCurrentCartId() {
    return localStorage.getItem(this.currentCartKey);
  }

  setCurrentCartId(cartId) {
    localStorage.setItem(this.currentCartKey, cartId);
  }

  async createCart(name = 'New Cart') {
    const cartId = this.generateId();
    const carts = this.getAllCarts();
    
    const currentCart = await this.getShopifyCart();
    
    carts[cartId] = {
      id: cartId,
      name: name,
      items: currentCart.items || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.saveCarts(carts);
    this.setCurrentCartId(cartId);
    
    return cartId;
  }

  async getShopifyCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }

  async clearShopifyCart() {
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  }

  async addItemsToShopifyCart(items) {
    if (!items || items.length === 0) return true;

    try {
      const formattedItems = items.map(item => ({
        id: item.variant_id || item.id,
        quantity: item.quantity,
        properties: item.properties || {}
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: formattedItems })
      });

      return response.ok;
    } catch (error) {
      console.error('Error adding items to cart:', error);
      return false;
    }
  }

  async saveCurrentCart(cartId) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    const shopifyCart = await this.getShopifyCart();
    carts[cartId].items = shopifyCart.items;
    carts[cartId].updatedAt = new Date().toISOString();
    
    this.saveCarts(carts);
    return true;
  }

  async switchCart(cartId) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    const currentCartId = this.getCurrentCartId();
    if (currentCartId && carts[currentCartId]) {
      await this.saveCurrentCart(currentCartId);
    }

    await this.clearShopifyCart();
    const newCart = carts[cartId];
    if (newCart.items && newCart.items.length > 0) {
      await this.addItemsToShopifyCart(newCart.items);
    }

    this.setCurrentCartId(cartId);
    window.location.reload();
    return true;
  }

  renameCart(cartId, newName) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    carts[cartId].name = newName;
    carts[cartId].updatedAt = new Date().toISOString();
    
    this.saveCarts(carts);
    return true;
  }

  async deleteCart(cartId) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    delete carts[cartId];
    this.saveCarts(carts);

    if (this.getCurrentCartId() === cartId) {
      const remainingCarts = Object.keys(carts);
      if (remainingCarts.length > 0) {
        await this.switchCart(remainingCarts[0]);
      } else {
        await this.createCart('My Cart');
      }
    }

    return true;
  }

  getCart(cartId) {
    const carts = this.getAllCarts();
    return carts[cartId] || null;
  }

  getCartItemCount(cartId) {
    const cart = this.getCart(cartId);
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  }

  async syncCurrentCart() {
    const currentCartId = this.getCurrentCartId();
    if (!currentCartId) return;
    await this.saveCurrentCart(currentCartId);
  }
}

// Initialize the multi-cart manager
window.multiCartManager = new MultiCartManager();
